import React from "react";
import { useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import {
  Box,
  Chip,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
} from "@material-ui/core";
import {
  Cancel as CancelIcon,
  CheckCircleOutline,
  ErrorOutline,
  Menu,
  PlayArrow,
  Save as SaveIcon,
  WarningRounded,
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import { useStyles } from "../FlowBuilder/flowEditorStyles";

const FlowToolbar = ({
  flowName,
  setFlowName,
  nodes,
  hasUnsavedChanges,
  saving,
  onSave,
  onCancel,
  onOpenDrawer,
  provider,
  lintResult,
  onToggleLintPanel,
  onPreview,
  previewDisabled,
  previewActive,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isTablet = useMediaQuery(theme.breakpoints.down("sm"));
  const isWaba = String(provider || "").toLowerCase() === "waba";

  return (
    <Toolbar className={classes.toolbar}>
      <Box display="flex" alignItems="center" style={{ gap: 8, flex: 1, minWidth: 0 }}>
        {isMobile && (
          <Tooltip title="Nodos">
            <IconButton
              size="small"
              onClick={onOpenDrawer}
              style={{ flexShrink: 0 }}
            >
              <Menu style={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        <TextField
          value={flowName}
          onChange={(e) => setFlowName(e.target.value)}
          placeholder={i18n.t("flowbuilders.editor.newFlow") || "Nuevo Flujo"}
          variant="outlined"
          size="small"
          style={{
            minWidth: isMobile ? "auto" : isTablet ? 180 : 300,
            flex: isMobile ? 1 : undefined,
            backgroundColor: theme.palette.background.paper,
          }}
          InputProps={{
            style: {
              fontWeight: 600,
              fontSize: isMobile ? 14 : 16,
            },
          }}
        />
        {nodes.length > 0 && !isMobile && (
          <Chip
            label={`${nodes.length} nodo${nodes.length > 1 ? "s" : ""}`}
            size="small"
            style={{
              backgroundColor: theme.palette.type === "dark"
                ? theme.palette.primary.dark
                : "#e3f2fd",
              color: theme.palette.type === "dark"
                ? theme.palette.primary.light
                : "#1976d2",
              fontWeight: 600,
              flexShrink: 0,
            }}
          />
        )}
        {provider && !isMobile && (
          <Tooltip
            title={
              isWaba
                ? "Flujo WhatsApp Cloud API (WABA): fuera de la ventana de 24 h solo se envían plantillas HSM."
                : "Flujo WhatsApp no oficial (Baileys/Evolution): texto, media y botones libres."
            }
          >
            <Chip
              label={isWaba ? "Canal: WABA" : "Canal: Baileys"}
              size="small"
              style={{
                backgroundColor: isWaba ? "#128C7E" : "#9e9e9e",
                color: "#fff",
                fontWeight: 600,
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box display="flex" alignItems="center" style={{ gap: 4, flexShrink: 0 }}>
        {lintResult && nodes.length > 0 && (
          <Tooltip
            title={
              lintResult.errors.length > 0
                ? i18n.t("flowbuilders.validation.errorsLabel", {
                    count: lintResult.errors.length,
                  })
                : lintResult.warnings.length > 0
                ? i18n.t("flowbuilders.validation.warningsLabel", {
                    count: lintResult.warnings.length,
                  })
                : i18n.t("flowbuilders.validation.allGood") ||
                  "El flujo no presenta problemas"
            }
          >
            <IconButton size="small" onClick={onToggleLintPanel}>
              {lintResult.errors.length > 0 ? (
                <ErrorOutline
                  style={{ color: theme.palette.error.main, fontSize: 22 }}
                />
              ) : lintResult.warnings.length > 0 ? (
                <WarningRounded
                  style={{ color: theme.palette.warning.main, fontSize: 22 }}
                />
              ) : (
                <CheckCircleOutline
                  style={{ color: theme.palette.success.main, fontSize: 22 }}
                />
              )}
            </IconButton>
          </Tooltip>
        )}
        {hasUnsavedChanges && !isMobile && (
          <Chip
            label="Sin guardar"
            size="small"
            style={{ backgroundColor: "#ff9800", color: "#fff", marginRight: 4 }}
          />
        )}
        {onPreview && (
          <Tooltip
            title={
              lintResult?.hasBlockingErrors
                ? i18n.t("flowbuilders.validation.blockedPreview") ||
                  "Corrige los errores antes de previsualizar el flujo"
                : i18n.t("flowbuilders.simulator.title") || "Previsualizar bot"
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={onPreview}
                disabled={previewDisabled || previewActive}
                style={{ marginRight: 4 }}
              >
                <PlayArrow
                  style={{
                    fontSize: 24,
                    color: previewDisabled
                      ? theme.palette.action.disabled
                      : theme.palette.success.main,
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={i18n.t("flowbuilders.editor.save") || "Guardar"}>
          <IconButton
            color="primary"
            onClick={onSave}
            disabled={saving}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "white",
            }}
          >
            <SaveIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={i18n.t("flowbuilders.editor.cancel") || "Cerrar"}>
          <IconButton
            onClick={onCancel}
            style={{ color: theme.palette.text.secondary }}
          >
            <CancelIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Toolbar>
  );
};

export default FlowToolbar;
